<script setup lang="ts">
import { watch, onMounted, computed } from 'vue'
import { useRoute } from 'vue-router'
import { storeToRefs } from 'pinia'
import { useRootStore } from '@/micro/store/root'
import { useLoginStore } from '@/micro/store/login'
import useUtils from '@/micro/composables/useUtils'
import useSeoMetadata from '@/micro/composables/useSeoMetadata'
import { COUNTRY_ALIAS, isNominaIcpRegisterCountry } from '@/micro/modules/version'
import type { ApplicationVersion } from '@/micro/types/version.type'

const route = useRoute()
const rootStore = useRootStore()
const loginStore = useLoginStore()
const { productName } = storeToRefs(rootStore)

const { setWindowTitle } = useUtils()
const { setSeoMetadata } = useSeoMetadata()

const LOGIN_ROUTES = [
  'login',
  'login-pos',
  'login-tpv',
  'login-pv',
  'login-payroll',
  'login-by-country',
  'login-payroll-by-country',
]

const noPadding = computed(() => {
  const routes = [
    'register-col-exp1',
    'register-dom-exp2',
    'register-cri-exp3',
    'register-costa-rica',
    'register-colombia',
    'register-republica-dominicana',
    'register-mexico',
  ]

  if (routes.includes(route.name as string)) return true

  if (route.name === 'register-payroll-by-country' && typeof route.params.country === 'string') {
    const country = (COUNTRY_ALIAS[route.params.country] ??
      route.params.country) as ApplicationVersion
    return isNominaIcpRegisterCountry(country)
  }

  return false
})

const hasLoginBanner = computed(
  () => LOGIN_ROUTES.includes(route.name as string) && loginStore.hasPublicityBanner,
)

onMounted(() => {
  setWindowTitle(route)
  setSeoMetadata(route)
})

watch(
  [() => route, () => productName],
  values => {
    setWindowTitle(values[0])
    setSeoMetadata(values[0])
  },
  {
    deep: true,
  },
)
</script>

<template>
  <div class="flex max-h-screen">
    <div class="flex flex-col w-full">
      <div
        class="flex w-full overflow-auto grow relative h-screen"
        :class="{ 'py-4': !noPadding && !hasLoginBanner }"
      >
        <slot></slot>
      </div>
    </div>
  </div>
</template>
